"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import {
  Sparkles,
  Wrench,
  Map as MapIcon,
  ClipboardCheck,
  Navigation,
  Gauge,
  Fuel,
  CalendarCheck,
  Timer,
  Receipt,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

/**
 * Soft Modern hero carousel — two slides: GPS fleet tracking and CMMS (نت).
 *
 * Autoplays every 6.5s, pauses on hover/focus and for prefers-reduced-motion,
 * prev/next + progress dots, RTL-safe via logical properties.
 *
 * Copy lives in messages/*.json under `home.hero.slides.gps` / `.cmms`
 * (see messages/hero.fa.snippet.json). Images: /public/soft-modern/hero-*.svg
 */

const SLIDES = [
  {
    key: "gps",
    Icon: Sparkles,
    PanelIcon: MapIcon,
    image: "/soft-modern/hero-gps.svg",
    secondaryHref: "/products",
    rows: [
      { key: "active", Icon: Navigation },
      { key: "speed", Icon: Gauge },
      { key: "fuel", Icon: Fuel },
    ],
  },
  {
    key: "cmms",
    Icon: Wrench,
    PanelIcon: ClipboardCheck,
    image: "/soft-modern/hero-cmms.svg",
    secondaryHref: "/pms",
    rows: [
      { key: "orders", Icon: CalendarCheck },
      { key: "downtime", Icon: Timer },
      { key: "cost", Icon: Receipt },
    ],
  },
] as const;

const INTERVAL = 6500;
const faDigits = (v: string | number) =>
  String(v).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

export default function HeroCarousel() {
  const t = useTranslations("home.hero");
  const locale = useLocale();
  const path = (href: string) => `/${locale}${href}`;
  const digits = locale === "fa" || locale === "ar" ? faDigits : String;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (paused || reduced.current) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), INTERVAL);
    return () => clearInterval(id);
  }, [paused, index]);

  const go = useCallback((i: number) => {
    setIndex((i + SLIDES.length) % SLIDES.length);
  }, []);

  const slide = SLIDES[index];
  const c = (k: string) => t(`slides.${slide.key}.${k}`);

  return (
    <section className="relative overflow-hidden">
      <div className="sm-blob sm-blob-teal pointer-events-none absolute -top-[22%] h-[620px] w-[620px] end-[-14%]" />
      <div
        className="sm-blob sm-blob-orange pointer-events-none absolute -bottom-[32%] h-[540px] w-[540px] start-[-10%]"
        style={{ animationDelay: "1.5s", animationDirection: "reverse" }}
      />

      <div className="relative mx-auto max-w-[1260px] px-4 pb-8 pt-9 sm:px-8 lg:px-11 lg:pb-14 lg:pt-[88px]">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
          {/* copy side */}
          <div
            className="min-w-0"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
          >
            <div key={slide.key} className="sm-in">
              <span className="sm-pill mb-[22px]">
                <slide.Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
                {c("kicker")}
              </span>

              <h1 className="m-0 mb-5 max-w-[20ch] text-[clamp(24px,3.4vw,42px)] font-extrabold leading-[1.26]">
                {c("titleLead")}
                <span className="text-[var(--sm-teal)]">{c("titleAccent")}</span>
                {c("titleMid")}
                <span className="sm-mark">{c("titleMark")}</span>
              </h1>

              <p className="m-0 mb-[30px] max-w-[52ch] text-[clamp(15.5px,1.3vw,18.5px)] leading-[1.95] text-[var(--sm-ink-3)]">
                {c("desc")}
              </p>

              <div className="mb-[26px] flex flex-wrap gap-3">
                <Link href={path("/contactus")} className="sm-btn">
                  <PhoneCall className="h-[18px] w-[18px]" strokeWidth={1.8} />
                  {c("cta")}
                </Link>
                <Link href={path(slide.secondaryHref)} className="sm-btn-ghost">
                  {c("secondary")}
                </Link>
              </div>
            </div>

            {/* controls */}
            <div className="mb-[26px] flex flex-wrap items-center gap-3.5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => go(index - 1)}
                  aria-label={t("prev")}
                  className="grid h-11 w-11 cursor-pointer place-items-center rounded-[13px] bg-white text-[var(--sm-ink)] shadow-[0_5px_16px_rgba(15,23,42,.09)] transition hover:-translate-y-0.5 hover:text-[var(--sm-teal-600)]"
                >
                  <ChevronRight className="h-[19px] w-[19px] rtl:block ltr:hidden" strokeWidth={1.8} />
                  <ChevronLeft className="h-[19px] w-[19px] ltr:block rtl:hidden" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={() => go(index + 1)}
                  aria-label={t("next")}
                  className="grid h-11 w-11 cursor-pointer place-items-center rounded-[13px] bg-white text-[var(--sm-ink)] shadow-[0_5px_16px_rgba(15,23,42,.09)] transition hover:-translate-y-0.5 hover:text-[var(--sm-teal-600)]"
                >
                  <ChevronLeft className="h-[19px] w-[19px] rtl:block ltr:hidden" strokeWidth={1.8} />
                  <ChevronRight className="h-[19px] w-[19px] ltr:block rtl:hidden" strokeWidth={1.8} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                {SLIDES.map((s, k) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => go(k)}
                    aria-label={t("goTo", { n: digits(k + 1) })}
                    aria-current={k === index}
                    className={`grid h-11 cursor-pointer place-items-center bg-transparent px-1 transition-[width] duration-300 ${
                      k === index ? "w-12" : "w-[22px]"
                    }`}
                  >
                    <span className="block h-[5px] w-full overflow-hidden rounded-full bg-[rgba(15,23,42,.12)]">
                      <span
                        className="block h-full origin-right rounded-full bg-[var(--sm-teal)] ltr:origin-left"
                        style={
                          k === index
                            ? {
                                animation: `smHeroBar ${INTERVAL}ms linear both`,
                                animationPlayState: paused ? "paused" : "running",
                              }
                            : { transform: "scaleX(0)" }
                        }
                      />
                    </span>
                  </button>
                ))}
              </div>

              <span className="sm-lat text-[12.5px] font-semibold text-[var(--sm-muted)]">
                {digits(index + 1)} / {digits(SLIDES.length)}
              </span>
            </div>

            <div className="flex flex-wrap gap-x-7 gap-y-2.5">
              {(["fleet", "years", "uptime", "support"] as const).map((k) => (
                <span key={k} className="flex flex-col">
                  <span className="sm-lat text-[25px] font-bold leading-[1.1] text-[var(--sm-ink)]">
                    {t(`stats.${k}.value`)}
                  </span>
                  <span className="mt-[3px] text-[12.5px] text-[var(--sm-muted)]">
                    {t(`stats.${k}.label`)}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* visual side */}
          <div className="min-w-0">
            <div className="overflow-hidden rounded-[26px] bg-white shadow-[var(--sm-sh-4)]">
              <div className="flex items-center justify-between gap-3.5 border-b border-[var(--sm-hairline)] px-5 py-[15px]">
                <span className="flex items-center gap-2.5 text-[13.5px] font-bold text-[var(--sm-ink-2)]">
                  <slide.PanelIcon className="h-[17px] w-[17px] text-[var(--sm-teal)]" strokeWidth={1.8} />
                  {c("panelTitle")}
                </span>
                <span className="inline-flex items-center gap-[7px] rounded-full bg-[var(--sm-teal-wash)] px-3 py-[5px] text-[12px] font-bold text-[var(--sm-teal-600)]">
                  <span className="h-[7px] w-[7px] rounded-full bg-[var(--sm-teal-400)]" />
                  {t("live")}
                </span>
              </div>

              <div key={slide.key} className="sm-in relative">
                <Image
                  src={slide.image}
                  alt={c("imageAlt")}
                  width={960}
                  height={600}
                  priority={index === 0}
                  className="block h-auto w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-px border-t border-[var(--sm-hairline)] bg-[var(--sm-hairline)] sm:grid-cols-3">
                {slide.rows.map((r) => (
                  <div key={r.key} className="bg-white px-[18px] py-4">
                    <span className="flex items-center gap-2 text-[12px] text-[var(--sm-muted)]">
                      <r.Icon className="h-[15px] w-[15px] text-[var(--sm-teal)]" strokeWidth={1.8} />
                      {c(`rows.${r.key}.label`)}
                    </span>
                    <span className="sm-lat mt-[5px] block text-[19px] font-bold">
                      {c(`rows.${r.key}.value`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
